import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    LinearProgress,
    IconButton,
    Stack,
    Tabs,
    Tab,
    Chip,
    Alert,
    Divider,
    Menu,
    MenuItem,
    ListItemText,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import { ArrowBack, ArrowForward, Home as HomeIcon, School, Menu as MenuIcon } from '@mui/icons-material';

interface TutorialExample {
    query_id: string;
    category: string;
    user_query: string;
    model_response: string;
    model: string;
    figures_info: string[];
    figs: string[];
    paper_title: string;
    pdf: string;
    annotation: {
        responses: Array<{
            questionId: string;
            answer: string | string[];
        }>;
        additionalNotes: string;
        timestamp: string;
    };
    annotation_rationale: {
        why_this_is_good?: string[];
        key_principle?: string;
        teaching_point?: string;
        contrast_with?: string;
    };
}

interface TutorialData {
    title: string;
    description: string;
    created: string;
    examples: {
        general: TutorialExample[];
        sections: TutorialExample[];
        figures: TutorialExample[];
    };
}

export const Tutorial = () => {
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [tutorialData, setTutorialData] = useState<TutorialData | null>(null);
    const [allExamples, setAllExamples] = useState<TutorialExample[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentFigureTab, setCurrentFigureTab] = useState(0);
    const [navMenuAnchor, setNavMenuAnchor] = useState<null | HTMLElement>(null);
    
    // Interactive tutorial state
    const [userResponses, setUserResponses] = useState<Record<string, string>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Load tutorial data
    useEffect(() => {
        loadTutorialData();
    }, []);

    const loadTutorialData = async () => {
        try {
            const response = await fetch('/api/tutorial-examples');
            if (!response.ok) {
                throw new Error('Failed to load tutorial examples');
            }
            const data: TutorialData = await response.json();
            setTutorialData(data);
            
            // Flatten all examples into a single array
            const examples = [
                ...data.examples.general,
                ...data.examples.sections,
                ...data.examples.figures,
            ];
            setAllExamples(examples);
            setLoading(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            setLoading(false);
        }
    };

    // Navigation handlers
    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setCurrentFigureTab(0);
            setUserResponses({});
            setIsSubmitted(false);
            window.scrollTo(0, 0);
        }
    };

    const handleNext = () => {
        if (currentIndex < allExamples.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setCurrentFigureTab(0);
            setUserResponses({});
            setIsSubmitted(false);
            window.scrollTo(0, 0);
        }
    };

    const handleJumpToExample = (index: number) => {
        setCurrentIndex(index);
        setCurrentFigureTab(0);
        setNavMenuAnchor(null);
        setUserResponses({});
        setIsSubmitted(false);
        window.scrollTo(0, 0);
    };

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setNavMenuAnchor(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setNavMenuAnchor(null);
    };

    // Handle user response changes
    const handleResponseChange = (questionId: string, answer: string) => {
        setUserResponses(prev => ({
            ...prev,
            [questionId]: answer
        }));
    };

    // Check if all questions are answered
    const allQuestionsAnswered = () => {
        const questions = getRubricQuestions();
        return questions
            .filter((q: any) => q.questionType === 'multiple-choice')
            .every((q: any) => userResponses[q.id]);
    };

    // Submit and check answers
    const handleSubmit = () => {
        if (!allQuestionsAnswered()) {
            return;
        }
        setIsSubmitted(true);
        window.scrollTo(0, document.body.scrollHeight);
    };

    // Check if a user's answer is correct
    const isAnswerCorrect = (questionId: string): boolean => {
        const correctAnswer = getAnswerForQuestion(questionId);
        const userAnswer = userResponses[questionId];
        return correctAnswer === userAnswer;
    };

    // Get rubric questions for current example
    const getRubricQuestions = useCallback(() => {
        if (!allExamples[currentIndex]) return [];

        const example = allExamples[currentIndex];
        const questions: any[] = [];
        const figuresInfo = example.figures_info || [];

        if (example.category === 'general') {
            figuresInfo.forEach((fig: string, idx: number) => {
                questions.push({
                    id: `general_fig_${idx + 1}`,
                    questionText: `Does ${fig} directly support one of the paper's primary conclusions?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'No',
                            followUpQuestions: [
                                {
                                    id: `general_fig_${idx + 1}_q1a_appropriate`,
                                    questionText: `If No: Is the out-of-scope figure or table appropriate per the query?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                                {
                                    id: `general_fig_${idx + 1}_q1b_explicitly_out_of_scope`,
                                    questionText: `If No: Does the response explicitly detail that the out-of-scope figure or table is out-of-scope?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                            ],
                        },
                    ],
                });
                questions.push({
                    id: `general_fig_${idx + 1}_q2_represents`,
                    questionText: `Does the response identify what ${fig} represents (e.g., “an infographic about X” or “a flow diagram representing Y”), rather than only citing it by number (e.g., “See ${fig}”)?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                });
            });
        } else if (example.category === 'sections') {
            figuresInfo.forEach((fig: string, idx: number) => {
                questions.push({
                    id: `sections_fig_${idx + 1}`,
                    questionText: `Looking at ${fig}, is the referenced figure or table directly scoped to the sections or methods the query asked about?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'No',
                            followUpQuestions: [
                                {
                                    id: `sections_fig_${idx + 1}_q1a_appropriate`,
                                    questionText: `If No, is this out-of-scope figure or table still appropriate for the query?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                                {
                                    id: `sections_fig_${idx + 1}_q1b_explicitly_out_of_scope`,
                                    questionText: `If No, does the response explicitly state that this figure or table is out-of-scope?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                            ],
                        },
                    ],
                });
                questions.push({
                    id: `sections_fig_${idx + 1}_q2_represents`,
                    questionText: `Does the response identify what ${fig} represents (e.g., “an infographic about X” or “a flow diagram representing Y”), rather than only citing it by number (e.g., “See ${fig}”)?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                });
            });
        } else if (example.category === 'figures') {
            // Mirror Annotate.tsx figure rubric structure (excluding instruction + long-text q5 for tutorial interactivity)
            figuresInfo.forEach((fig: string, idx: number) => {
                const baseId = `figures_fig_${idx + 1}`;

                questions.push({
                    id: `${baseId}_q1_accuracy`,
                    questionText: `1. Are all described elements for ${fig} factually correct?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                });

                questions.push({
                    id: `${baseId}_q2_multipart`,
                    questionText: `2. Is ${fig} multi-part (e.g., 1a, 1b, …, 1f)?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'Yes',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q2a_subpart_referenced`,
                                    questionText: `2A. Is at least one sub-part of ${fig} (expicitly or implicitly) referenced in the response (e.g., 1a, or 1d)?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                    conditionalFollowUps: [
                                        {
                                            triggerAnswer: 'Yes',
                                            followUpQuestions: [
                                                {
                                                    id: `${baseId}_q2a_i_all_subparts`,
                                                    questionText: `2A.i. Are all other sub-parts of ${fig} mentioned or described (even if briefly)?`,
                                                    questionType: 'multiple-choice',
                                                    options: ['Yes', 'No'],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                });

                questions.push({
                    id: `${baseId}_q3_visual_order`,
                    questionText: `3. Is there a natural reading or visual order in ${fig} (e.g., top to bottom, left to right, labeled sequence of steps)?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'Yes',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q3a_follows_order`,
                                    questionText: `3A. Does the response describe ${fig} in that natural visual order?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                            ],
                        },
                    ],
                });

                questions.push({
                    id: `${baseId}_q4_graph_trend`,
                    questionText: `4. Does ${fig} contain a graph, chart, or trend?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'Yes',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q4a_axis`,
                                    questionText: `4A. Is there an axis, or some other measurement/metrics associated with ${fig}?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                    conditionalFollowUps: [
                                        {
                                            triggerAnswer: 'Yes',
                                            followUpQuestions: [
                                                {
                                                    id: `${baseId}_q4a_i_bounds`,
                                                    questionText: `4A.i. Does the model response specify the bounds of the axis / measurement units for ${fig}?`,
                                                    questionType: 'multiple-choice',
                                                    options: ['Yes', 'No'],
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    id: `${baseId}_q4b_trend_direction`,
                                    questionText: `4B. Does the response describe the direction / shape of the trend in ${fig}?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                                {
                                    id: `${baseId}_q4c_missing_elements`,
                                    questionText: `4C. Does the response have any missing elements necessary for interpretation of the trend in ${fig} if unable to visually access the figure?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                            ],
                        },
                    ],
                });

                questions.push({
                    id: `${baseId}_q5_identify_components`,
                    questionText: `5. Identify the important components of ${fig} necessary for scientific interpretation. Please list each component and rationalize why it is necessary.`,
                    questionType: 'long-text',
                });

                questions.push({
                    id: `${baseId}_q5a_components_described`,
                    questionText: `5A. For each essential component identified above, is the component described in the response?`,
                    questionType: 'multiple-choice',
                    options: ['None', 'Some', 'All'],
                });

                questions.push({
                    id: `${baseId}_q6_visual_semantics`,
                    questionText: `6. Are visual semantics such as color, shapes, sizes, etc. important for interpreting ${fig}?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'Yes',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q6a_semantics_described`,
                                    questionText: `6A. Are all relevant visual semantics described in the response?`,
                                    questionType: 'multiple-choice',
                                    options: ['None', 'Some', 'All'],
                                },
                            ],
                        },
                    ],
                });

                questions.push({
                    id: `${baseId}_q7_visual_detail_requested`,
                    questionText: `7. Was visual detail (visual semantics, specific components) explicitly requested in the user query?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'No',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q7a_includes_visual_details`,
                                    questionText: `7A. Does the response include visual details about ${fig}?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                    conditionalFollowUps: [
                                        {
                                            triggerAnswer: 'Yes',
                                            followUpQuestions: [
                                                {
                                                    id: `${baseId}_q7a_i_necessary_details`,
                                                    questionText: `7A.i. Are the included visual details all necessary to interpret the key results in ${fig}? (This is an atomic answer – if even one visual detail that was included is not necessary, mark No)`,
                                                    questionType: 'multiple-choice',
                                                    options: ['Yes', 'No'],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                });
            });
        }

        // Match Annotate page behavior: only show conditional follow-ups when triggered
        const visibleQuestions: any[] = [];
        const processQuestion = (question: any) => {
            visibleQuestions.push(question);

            if (question.conditionalFollowUps) {
                const currentAnswer = userResponses[question.id];
                const matchingFollowUp = question.conditionalFollowUps.find(
                    (followUp: any) => followUp.triggerAnswer === currentAnswer
                );

                if (matchingFollowUp) {
                    matchingFollowUp.followUpQuestions.forEach(processQuestion);
                }
            }
        };

        questions.forEach(processQuestion);
        return visibleQuestions;
    }, [allExamples, currentIndex, userResponses]);

    const getAnswerForQuestion = (questionId: string): string => {
        if (!allExamples[currentIndex]) return '';

        const responses = allExamples[currentIndex].annotation.responses;
        const direct = responses.find(r => r.questionId === questionId);
        if (direct) return String(direct.answer);

        // Backwards-compatible mapping for legacy tutorial IDs in figures examples
        const legacyAliases: Record<string, string[]> = {
            '_q3a_follows_order': ['_q3a_describes_in_order'],
            '_q4_graph_trend': ['_q4_graph_or_trend'],
            '_q4a_axis': ['_q4a_axis_metrics'],
            '_q4a_i_bounds': ['_q4a_i_bounds_specified'],
            '_q5_identify_components': ['_q5_components'],
            '_q5a_components_described': ['_q5a_component_coverage'],
            '_q7_visual_detail_requested': ['_q7_details_requested'],
        };

        for (const [newSuffix, oldSuffixes] of Object.entries(legacyAliases)) {
            if (questionId.endsWith(newSuffix)) {
                for (const oldSuffix of oldSuffixes) {
                    const oldId = questionId.replace(newSuffix, oldSuffix);
                    const legacy = responses.find(r => r.questionId === oldId);
                    if (legacy) return String(legacy.answer);
                }
            }
        }

        return '';
    };

    // Get questions grouped by figure (for figures category with tabs)
    const getRubricQuestionsByFigure = useCallback((): { figureName: string; questions: any[] }[] => {
        if (!allExamples[currentIndex]) return [];
        
        const currentExample = allExamples[currentIndex];
        if (currentExample.category !== 'figures') return [];
        
        const figuresInfo = currentExample.figures_info || [];
        const groupedQuestions: { figureName: string; questions: any[] }[] = [];
        
        figuresInfo.forEach((fig: string, idx: number) => {
            const baseId = `figures_fig_${idx + 1}`;
            const figQuestions: any[] = [];
            
            // Get all questions for this specific figure from the rubric
            const allRubricQuestions = getRubricQuestions();
            
            // Filter questions that belong to this figure
            allRubricQuestions.forEach((q: any) => {
                if (q.id.startsWith(baseId)) {
                    figQuestions.push(q);
                }
            });
            
            groupedQuestions.push({
                figureName: fig,
                questions: figQuestions,
            });
        });
        
        return groupedQuestions;
    }, [currentIndex, allExamples, getRubricQuestions]);

    if (loading) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Loading tutorial examples...
                </Typography>
                <LinearProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">
                    Error loading tutorial: {error}
                </Alert>
                <Button onClick={() => navigate('/')} startIcon={<HomeIcon />} sx={{ mt: 2 }}>
                    Back to Home
                </Button>
            </Box>
        );
    }

    if (!allExamples.length) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="info">
                    No tutorial examples available.
                </Alert>
                <Button onClick={() => navigate('/')} startIcon={<HomeIcon />} sx={{ mt: 2 }}>
                    Back to Home
                </Button>
            </Box>
        );
    }

    const currentExample = allExamples[currentIndex];
    const rubricQuestions = getRubricQuestions();
    const progress = ((currentIndex + 1) / allExamples.length) * 100;

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header with Tutorial Mode indicator */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: '#FFF9E6', borderTop: 3, borderTopColor: '#FFD54F' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <School color="primary" />
                        <Typography variant="h6" color="primary">
                            Tutorial Mode - Example {currentIndex + 1} of {allExamples.length}
                        </Typography>
                        <Chip label={currentExample.category.toUpperCase()} color="primary" size="small" />
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<MenuIcon />}
                            onClick={handleOpenNavMenu}>
                            Jump to Example
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<HomeIcon />}
                            onClick={() => navigate('/')}>
                            Back to Home
                        </Button>
                    </Stack>
                </Stack>
                <LinearProgress variant="determinate" value={progress} />
            </Box>

            {/* Navigation Menu */}
            <Menu
                anchorEl={navMenuAnchor}
                open={Boolean(navMenuAnchor)}
                onClose={handleCloseNavMenu}
                PaperProps={{
                    style: {
                        maxHeight: 400,
                    },
                }}
            >
                {tutorialData && (
                    <>
                        <MenuItem disabled>
                            <ListItemText primary="General Examples" primaryTypographyProps={{ fontWeight: 'bold', color: 'primary' }} />
                        </MenuItem>
                        {tutorialData.examples.general.map((ex, idx) => (
                            <MenuItem 
                                key={`general-${idx}`}
                                onClick={() => handleJumpToExample(idx)}
                                selected={currentIndex === idx}
                            >
                                <ListItemText 
                                    primary={`Example ${idx + 1}`}
                                    secondary={ex.user_query.substring(0, 50) + '...'}
                                />
                            </MenuItem>
                        ))}
                        <Divider />
                        <MenuItem disabled>
                            <ListItemText primary="Sections Examples" primaryTypographyProps={{ fontWeight: 'bold', color: 'primary' }} />
                        </MenuItem>
                        {tutorialData.examples.sections.map((ex, idx) => {
                            const globalIdx = tutorialData.examples.general.length + idx;
                            return (
                                <MenuItem 
                                    key={`sections-${idx}`}
                                    onClick={() => handleJumpToExample(globalIdx)}
                                    selected={currentIndex === globalIdx}
                                >
                                    <ListItemText 
                                        primary={`Example ${globalIdx + 1}`}
                                        secondary={ex.user_query.substring(0, 50) + '...'}
                                    />
                                </MenuItem>
                            );
                        })}
                        <Divider />
                        <MenuItem disabled>
                            <ListItemText primary="Figures Examples" primaryTypographyProps={{ fontWeight: 'bold', color: 'primary' }} />
                        </MenuItem>
                        {tutorialData.examples.figures.map((ex, idx) => {
                            const globalIdx = tutorialData.examples.general.length + tutorialData.examples.sections.length + idx;
                            return (
                                <MenuItem 
                                    key={`figures-${idx}`}
                                    onClick={() => handleJumpToExample(globalIdx)}
                                    selected={currentIndex === globalIdx}
                                >
                                    <ListItemText 
                                        primary={`Example ${globalIdx + 1}`}
                                        secondary={ex.user_query.substring(0, 50) + '...'}
                                    />
                                </MenuItem>
                            );
                        })}
                    </>
                )}
            </Menu>

            {/* Main Content */}
            <Grid container sx={{ flex: 1, width: '100%', minHeight: 0 }}>
                {/* Left Panel - Query, Response, Figures */}
                <Grid 
                    item 
                    xs={6}
                    sx={{ 
                        overflow: 'auto', 
                        p: 3,
                        borderRight: 1,
                        borderColor: 'divider',
                        height: '100%',
                        width: '50%',
                        flexBasis: '50%'
                    }}
                >
                    <Stack spacing={3}>
                        {/* Paper Information - Prominent at top */}
                        {(currentExample.paper_title || currentExample.pdf) && (
                            <Paper elevation={2} sx={{ p: 3, bgcolor: '#f5f5f5', border: 2, borderColor: 'primary.main' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        {currentExample.paper_title && (
                                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                                {currentExample.paper_title}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                            Source Paper
                                        </Typography>
                                    </Box>
                                    {currentExample.pdf && (
                                        <Button
                                            variant="contained"
                                            href={currentExample.pdf}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="small"
                                        >
                                            View PDF
                                        </Button>
                                    )}
                                </Stack>
                            </Paper>
                        )}

                        {/* Query */}
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Query
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Category: {currentExample.category}
                            </Typography>
                            <Typography variant="body1">{currentExample.user_query}</Typography>
                        </Paper>

                        {/* Response */}
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Response
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {currentExample.model_response}
                            </Typography>
                        </Paper>

                        {/* Associated Figures */}
                        {currentExample.figs && currentExample.figs.length > 0 && (
                            <Paper elevation={2} sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    Associated Figures
                                </Typography>
                                {currentExample.figures_info && currentExample.figures_info.length > 0 && (
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Referenced: {currentExample.figures_info.join(', ')}
                                    </Typography>
                                )}
                                <Stack spacing={2} sx={{ mt: 2 }}>
                                    {currentExample.figs.map((figUrl: string, idx: number) => (
                                        <Box key={idx}>
                                            <Typography variant="caption" color="text.secondary">
                                                {currentExample.figures_info?.[idx] || `Figure ${idx + 1}`}
                                            </Typography>
                                            <Box
                                                component="img"
                                                src={figUrl}
                                                alt={`Figure ${idx + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    maxHeight: 400,
                                                    objectFit: 'contain',
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    mt: 1,
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </Grid>

                {/* Right Panel - Interactive Rubric */}
                <Grid 
                    item 
                    xs={6}
                    sx={{ 
                        overflow: 'auto', 
                        p: 3,
                        height: '100%',
                        width: '50%',
                        flexBasis: '50%',
                        bgcolor: 'background.default'
                    }}
                >
                    <Stack spacing={3}>
                        {/* Tutorial Info Banner */}
                        <Alert severity="info" icon={<School />}>
                            <Typography variant="subtitle2" gutterBottom>
                                <strong>Interactive Tutorial</strong>
                            </Typography>
                            <Typography variant="body2">
                                {!isSubmitted 
                                    ? "Answer the rubric questions below, then submit to see if your answers match the expert annotation."
                                    : "Great! Compare your answers with the expert annotation below."}
                            </Typography>
                        </Alert>

                        <Typography variant="h5" gutterBottom>
                            {isSubmitted ? 'Your Results' : 'Answer the Rubric Questions'}
                        </Typography>

                        {/* Rubric Questions - Interactive or with Feedback */}
                        {currentExample.category === 'figures' && currentExample.figures_info && currentExample.figures_info.length > 1 ? (
                            // Multiple figures - use tabs
                            <Box>
                                <Tabs 
                                    value={currentFigureTab} 
                                    onChange={(e, newValue) => setCurrentFigureTab(newValue)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    {getRubricQuestionsByFigure().map((group, idx) => (
                                        <Tab key={idx} label={group.figureName} />
                                    ))}
                                </Tabs>
                                
                                {getRubricQuestionsByFigure().map((group, idx) => (
                                    <Box 
                                        key={idx} 
                                        role="tabpanel" 
                                        hidden={currentFigureTab !== idx}
                                        sx={{ pt: 3 }}
                                    >
                                        {currentFigureTab === idx && (
                                            <Stack spacing={3}>
                                                {group.questions.map((question: any) => {
                                                    const correctAnswer = getAnswerForQuestion(question.id);
                                                    const userAnswer = userResponses[question.id];
                                                    const isCorrect = isAnswerCorrect(question.id);
                                                    const isMultipleChoice = question.questionType === 'multiple-choice';
                                                    
                                                    return (
                                                        <Paper key={question.id} elevation={1} sx={{ p: 3, bgcolor: 'white' }}>
                                                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                                                {question.questionText}
                                                            </Typography>
                                                            
                                                            {!isSubmitted ? (
                                                                isMultipleChoice ? (
                                                                    // Interactive mode - multiple-choice
                                                                    <FormControl component="fieldset" sx={{ mt: 2 }}>
                                                                        <RadioGroup
                                                                            value={userAnswer || ''}
                                                                            onChange={(e) => handleResponseChange(question.id, e.target.value)}
                                                                        >
                                                                            {question.options?.map((option: string) => (
                                                                                <FormControlLabel
                                                                                    key={option}
                                                                                    value={option}
                                                                                    control={<Radio />}
                                                                                    label={option}
                                                                                />
                                                                            ))}
                                                                        </RadioGroup>
                                                                    </FormControl>
                                                                ) : (
                                                                    // Interactive mode - long-text
                                                                    <TextField
                                                                        fullWidth
                                                                        multiline
                                                                        rows={4}
                                                                        value={userAnswer || ''}
                                                                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                                                                        placeholder="Enter your answer..."
                                                                        sx={{ mt: 2 }}
                                                                    />
                                                                )
                                                            ) : (
                                                                isMultipleChoice ? (
                                                                    // Submitted mode - multiple-choice feedback
                                                                    <Box sx={{ 
                                                                        mt: 2, 
                                                                        p: 2, 
                                                                        bgcolor: isCorrect ? 'success.light' : 'error.light', 
                                                                        borderRadius: 1, 
                                                                        border: 2, 
                                                                        borderColor: isCorrect ? 'success.main' : 'error.main' 
                                                                    }}>
                                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                            {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
                                                                        </Typography>
                                                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                                            Your answer: {userAnswer || 'Not answered'}
                                                                        </Typography>
                                                                        {!isCorrect && (
                                                                            <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                                                                                Correct answer: {correctAnswer}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                ) : (
                                                                    // Submitted mode - long-text reference
                                                                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1, border: 1, borderColor: 'info.main' }}>
                                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                            Your answer
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                                            {userAnswer || 'Not answered'}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                            Reference answer
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            {correctAnswer || 'No reference answer provided.'}
                                                                        </Typography>
                                                                    </Box>
                                                                )
                                                            )}
                                                        </Paper>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            // Single figure or non-figures category - no tabs
                            rubricQuestions.map((question: any) => {
                                const correctAnswer = getAnswerForQuestion(question.id);
                                const userAnswer = userResponses[question.id];
                                const isCorrect = isAnswerCorrect(question.id);
                                const isMultipleChoice = question.questionType === 'multiple-choice';
                                
                                return (
                                    <Paper key={question.id} elevation={1} sx={{ p: 3, bgcolor: 'white' }}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                            {question.questionText}
                                        </Typography>
                                        
                                        {!isSubmitted ? (
                                            isMultipleChoice ? (
                                                // Interactive mode - multiple-choice
                                                <FormControl component="fieldset" sx={{ mt: 2 }}>
                                                    <RadioGroup
                                                        value={userAnswer || ''}
                                                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                                                    >
                                                        {question.options?.map((option: string) => (
                                                            <FormControlLabel
                                                                key={option}
                                                                value={option}
                                                                control={<Radio />}
                                                                label={option}
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                </FormControl>
                                            ) : (
                                                // Interactive mode - long-text
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    value={userAnswer || ''}
                                                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                                                    placeholder="Enter your answer..."
                                                    sx={{ mt: 2 }}
                                                />
                                            )
                                        ) : (
                                            isMultipleChoice ? (
                                                // Submitted mode - multiple-choice feedback
                                                <Box sx={{ 
                                                    mt: 2, 
                                                    p: 2, 
                                                    bgcolor: isCorrect ? 'success.light' : 'error.light', 
                                                    borderRadius: 1, 
                                                    border: 2, 
                                                    borderColor: isCorrect ? 'success.main' : 'error.main' 
                                                }}>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                        Your answer: {userAnswer || 'Not answered'}
                                                    </Typography>
                                                    {!isCorrect && (
                                                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                                                            Correct answer: {correctAnswer}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : (
                                                // Submitted mode - long-text reference
                                                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1, border: 1, borderColor: 'info.main' }}>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Your answer
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        {userAnswer || 'Not answered'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Reference answer
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {correctAnswer || 'No reference answer provided.'}
                                                    </Typography>
                                                </Box>
                                            )
                                        )}
                                    </Paper>
                                );
                            })
                        )}

                        {/* Submit Button */}
                        {!isSubmitted && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={!allQuestionsAnswered()}
                                sx={{ mt: 2 }}
                            >
                                Submit Answers
                            </Button>
                        )}

                        {/* Show rationale only after submission */}
                        {isSubmitted && (
                            <>
                                {/* Additional Notes */}
                                {currentExample.annotation.additionalNotes && (
                                    <Paper elevation={1} sx={{ p: 3, bgcolor: '#E3F2FD', border: 1, borderColor: '#90CAF9' }}>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Annotation Notes
                                        </Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {currentExample.annotation.additionalNotes}
                                        </Typography>
                                    </Paper>
                                )}

                                {/* Rationale */}
                                {currentExample.annotation_rationale && (
                                    <Paper elevation={2} sx={{ p: 3, bgcolor: '#FFF9E6', border: 2, borderColor: '#FFD54F' }}>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            📚 Teaching Points
                                        </Typography>
                                        
                                        {currentExample.annotation_rationale.key_principle && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                    Key Principle:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {currentExample.annotation_rationale.key_principle}
                                                </Typography>
                                            </Box>
                                        )}

                                        {currentExample.annotation_rationale.why_this_is_good && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                    Why this is correct:
                                                </Typography>
                                                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                                                    {currentExample.annotation_rationale.why_this_is_good.map((reason: string, idx: number) => (
                                                        <li key={idx}>
                                                            <Typography variant="body2">{reason}</Typography>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </Box>
                                        )}

                                        {currentExample.annotation_rationale.teaching_point && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                    Teaching Point:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {currentExample.annotation_rationale.teaching_point}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                )}
                            </>
                        )}
                    </Stack>
                </Grid>
            </Grid>

            {/* Navigation Footer */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'background.paper' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}>
                        Previous Example
                    </Button>

                    <Typography variant="body2" color="text.secondary">
                        Example {currentIndex + 1} of {allExamples.length} • {currentExample.category} category
                    </Typography>

                    <Button
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={handleNext}
                        disabled={currentIndex === allExamples.length - 1 || !isSubmitted}>
                        Next Example
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
};
